import { useState } from 'react';
import { Rocket, Check, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const PACKAGES = [
  { type: 'audit', name: 'Digital Presence Audit', price: 149, points: ['Five-parameter BDAF audit', 'Written report with fixes', 'UKSPF-eligible'] },
  { type: 'listing', name: 'Verification + Listing', price: 299, points: ['Everything in Audit', 'Google Business Profile set-up', '3 directory listings', '30-day re-assessment'], recommended: true },
  { type: 'uplift', name: 'Full Tier Uplift Programme', price: 499, points: ['Everything in Listing', 'Complete intervention plan', '30 & 90-day re-assessment', 'Tier Uplift Certificate'] },
];

export const BoostBusinessButton = ({ business, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState('');

  const buy = async (type) => {
    setBusy(type);
    try {
      const { data } = await api.post('/billing/checkout/commission', {
        items: [{ business_id: business.id, business_name: business.company_name, commission_type: type }],
        origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else toast.error('Checkout could not be started');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout could not be started');
    } finally { setBusy(''); }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} data-testid="boost-business-btn" className={`bg-brand-orange hover:bg-highlight-orange text-white font-body text-sm font-semibold ${className}`}>
        <Rocket className="w-4 h-4 mr-1.5" strokeWidth={2} /> Boost my business
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="boost-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold text-ink">Boost {business.company_name}</DialogTitle>
            <p className="font-body text-xs text-ink/60">Done-for-you digital uplift by the Adoptic team. One-off payment, prices exclude VAT (20% added at checkout). Delivered across the whole Tees Valley.</p>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            {PACKAGES.map(p => (
              <div key={p.type} data-testid={`boost-pkg-${p.type}`} className={`rounded-[10px] p-4 flex flex-col ${p.recommended ? 'bg-ink text-white' : 'bg-white border border-ink/10'}`}>
                {p.recommended && <span className="self-start font-body text-[10px] uppercase tracking-wider bg-brand-orange text-white px-2 py-0.5 rounded-full mb-2">Most popular</span>}
                <p className={`font-display text-base font-extrabold ${p.recommended ? 'text-white' : 'text-ink'}`}>{p.name}</p>
                <p className={`font-display text-2xl font-extrabold mt-1 ${p.recommended ? 'text-white' : 'text-ink'}`}>£{p.price}<span className={`font-body text-xs font-normal ml-1 ${p.recommended ? 'text-white/60' : 'text-ink/50'}`}>+ VAT</span></p>
                <ul className="mt-3 space-y-1.5 flex-1">
                  {p.points.map(pt => <li key={pt} className={`flex items-start gap-1.5 font-body text-xs ${p.recommended ? 'text-white/80' : 'text-ink/70'}`}><Check className="w-3.5 h-3.5 mt-0.5 text-brand-orange shrink-0" strokeWidth={3} />{pt}</li>)}
                </ul>
                <Button onClick={() => buy(p.type)} disabled={!!busy} data-testid={`boost-buy-${p.type}`} className={`mt-4 w-full font-body text-xs ${p.recommended ? 'bg-brand-orange hover:bg-highlight-orange text-white' : 'bg-ink hover:bg-ink/90 text-white'}`}>
                  {busy === p.type ? 'Opening checkout…' : 'Buy now'} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
